import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Bus, MapPin, Zap, Clock, Calendar, AlertCircle, Lightbulb, Mountain } from "lucide-react";
import { useData } from "../lib/data-context";
import { fmtInt, fmtDec, fmtMWh } from "../lib/api";
import { SemaforoLarge } from "../components/Semaforo";
import MatrizTipologias from "../components/MatrizTipologias";
import ConsumoTipoDia from "../components/ConsumoTipoDia";
import { ViabilidadScoreCard } from "../components/ViabilidadLetra";

export default function FichaLinea() {
  const { codigo } = useParams();
  const nav = useNavigate();
  const { lineas, loading } = useData();

  if (loading) return <div className="text-gray-500">Cargando…</div>;

  const linea = lineas?.find((l) => l.codigo_linea === decodeURIComponent(codigo));
  if (!linea) {
    return (
      <div className="crtm-card p-8 text-center" data-testid="linea-no-encontrada">
        <p className="text-gray-700">Línea no encontrada.</p>
        <button onClick={() => nav("/tabla")} className="btn-primary mt-4">Volver a tabla</button>
      </div>
    );
  }

  const condicionantes = [];
  if (linea.tamano_vehiculo === "18 m") condicionantes.push("Vehículo articulado de 18 m: alta demanda energética y necesidad de baterías de gran capacidad.");
  if (linea.tipo_servicio === "Urbano") condicionantes.push("Servicio urbano: número de paradas elevado y arranques/frenadas frecuentes.");
  if (linea.tipo_servicio === "Nocturno") condicionantes.push("Servicio nocturno: limita la ventana de carga en cochera; planificar reservas de batería.");
  if (linea.tipo_servicio === "Interurbano") condicionantes.push("Servicio interurbano: distancias largas; verificar autonomía y carga en cabecera.");
  if (linea.longitud_km > 25) condicionantes.push("Longitud por sentido elevada (>25 km): vigilar autonomía con margen de seguridad.");
  if (linea.consumo_kwh_km_base >= 2.0) condicionantes.push("Factor kWh/km elevado: priorizar vehículos con eficiencia certificada.");
  if (condicionantes.length === 0) condicionantes.push("Sin condicionantes operativos relevantes identificados.");

  const recomendaciones = [];
  if (linea.viabilidad === "Alta") {
    recomendaciones.push("Electrificación viable con carga lenta nocturna en cochera (50-80 kW por plaza).");
    recomendaciones.push("Vehículos eléctricos comerciales estándar con batería 350-450 kWh.");
  } else if (linea.viabilidad === "Condicionada") {
    recomendaciones.push("Combinar carga en cochera con carga de oportunidad en cabecera (150-300 kW).");
    recomendaciones.push("Reservar batería mínima del 15-20% para imprevistos operativos.");
    recomendaciones.push("Validar pendientes y ruta real con telemetría antes de homologar la línea.");
  } else {
    recomendaciones.push("Considerar pantógrafo en cabecera o segregar parte del servicio.");
    recomendaciones.push("Estudio detallado de ruta y telemetría obligatorio.");
    recomendaciones.push("Alternativa: vehículos de batería extendida (>550 kWh) o hidrógeno como complemento.");
  }

  return (
    <div className="space-y-6" data-testid="ficha-linea">
      <button onClick={() => nav(-1)} className="text-gray-600 text-sm flex items-center gap-1 hover:text-gray-900">
        <ArrowLeft size={15} /> Volver
      </button>

      <div className="crtm-card p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="pill neutral">{linea.lote}</span>
              <span className="pill neutral">{linea.tipo_servicio}</span>
              <span className="pill neutral">{linea.etiqueta_ambiental}</span>
            </div>
            <h1 className="font-heading font-bold text-3xl text-gray-900 tracking-tight">
              Línea {linea.numero_linea}
            </h1>
            <p className="text-gray-600 mt-1">{linea.denominacion}</p>
            <p className="text-xs font-mono text-gray-400 mt-1">{linea.codigo_linea}</p>
          </div>
          <SemaforoLarge viabilidad={linea.viabilidad} />
        </div>
      </div>

      {/* Grid datos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <DataGroup
          icon={Bus}
          title="Vehículo asignado"
          items={[
            ["Tipología (Anexo VIII)", linea.tipologia_vehiculo],
            ["Clase", linea.clase_vehiculo],
            ["Tamaño", linea.tamano_vehiculo],
            ["Etiqueta ambiental", linea.etiqueta_ambiental],
          ]}
        />
        <DataGroup
          icon={MapPin}
          title="Operación"
          items={[
            ["Tipo de servicio", linea.tipo_servicio],
            ["Longitud por sentido", `${fmtDec(linea.longitud_km, 2)} km`],
            ["Expediciones/año", fmtInt(linea.expediciones_anuales)],
            ["Veh-km anuales", fmtInt(linea.veh_km_anuales)],
          ]}
        />
        <DataGroup
          icon={Zap}
          title="Consumo energético"
          items={[
            ["Factor kWh/km base", `${fmtDec(linea.consumo_kwh_km_base, 2)} kWh/km`],
            ["Factor kWh/km cons.", `${fmtDec(linea.consumo_kwh_km_conservador, 2)} kWh/km`],
            ["Consumo por expedición", `${fmtDec(linea.consumo_por_expedicion_kwh, 1)} kWh`],
            ["Consumo diario estimado", `${fmtInt(linea.consumo_diario_estimado_kwh)} kWh`],
          ]}
        />
      </div>

      <div className="crtm-card p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="kpi-label">Consumo anual estimado</div>
            <div className="kpi-value mt-1">{fmtInt(linea.consumo_anual_estimado_kwh)} kWh</div>
            <div className="text-sm text-gray-500 mt-0.5">{fmtMWh(linea.consumo_anual_estimado_kwh)}</div>
          </div>
          <div>
            <div className="kpi-label">Equivalencia</div>
            <div className="text-base text-gray-800 mt-1">
              {fmtDec(linea.consumo_anual_estimado_kwh / 350, 0)} kWh/día medio
            </div>
            <div className="text-xs text-gray-500">
              ≈ {fmtDec((linea.consumo_anual_estimado_kwh / linea.veh_km_anuales) * 100, 1)} kWh/100km
            </div>
          </div>
        </div>
      </div>

      {/* Orografía */}
      {linea.ascenso_por_km != null && (
        <div className="crtm-card p-6" data-testid="orografia-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
              <Mountain size={15} strokeWidth={1.75} className="text-gray-700" />
            </div>
            <h3 className="font-heading font-semibold text-[15px]">Orografía del recorrido</h3>
            <span className="ml-auto text-[10px] text-gray-400 tracking-wider uppercase font-semibold">
              SRTM DEM · muestreo cada 50 m
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <OroStat label="Ascenso acumulado" value={`${fmtInt(linea.ascenso_total_m || 0)} m`} accent="up" />
            <OroStat label="Descenso acumulado" value={`${fmtInt(linea.descenso_total_m || 0)} m`} accent="down" />
            <OroStat label="Desnivel por km" value={`${fmtDec(linea.ascenso_por_km || 0, 1)} m/km`} highlight />
            <OroStat label="Elevación mín." value={`${fmtInt(linea.elevacion_min_m || 0)} m`} />
            <OroStat label="Elevación máx." value={`${fmtInt(linea.elevacion_max_m || 0)} m`} />
          </div>
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            El desnivel acumulado positivo (m/km) se utiliza como penalización en el escenario al activar
            "Aplicar orografía real (SRTM DEM)". Cada m/km añade un coste energético adicional al factor base kWh/km.
          </p>
        </div>
      )}

      {/* Score de viabilidad eléctrica (sistema 0-10 + letra A+/A/B/C/D) */}
      <ViabilidadScoreCard linea={linea} />

      {/* Matriz de tipologías alternativas (propuesta - ajuste de alcance) */}
      <MatrizTipologias codigoLinea={linea.codigo_linea} />

      {/* Consumo por tipo de día — específico de esta línea */}
      <ConsumoTipoDia
        codigoLinea={linea.codigo_linea}
        title={`Consumo por tipo de día — Línea ${linea.numero_linea}`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ListCard title="Principales condicionantes" icon={AlertCircle} items={condicionantes} tone="amber" />
        <ListCard title="Recomendaciones técnicas" icon={Lightbulb} items={recomendaciones} tone="blue" />
      </div>

      <div className="crtm-card p-6">
        <h3 className="font-heading font-semibold text-[15px] mb-2">Observaciones del estudio</h3>
        <p className="text-sm text-gray-700 leading-relaxed">{linea.observaciones}</p>
        <p className="text-xs text-gray-400 mt-3">
          Fuente: {linea.origen_datos === "anteproyecto" ? "Anteproyecto del operador (Anexo VIII)" : linea.origen_datos === "simulado" ? "Datos simulados" : "Datos cargados por usuario"}.
        </p>
      </div>
    </div>
  );
}

const DataGroup = ({ icon: Icon, title, items }) => (
  <div className="crtm-card p-5">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
        <Icon size={15} strokeWidth={1.75} className="text-gray-700" />
      </div>
      <h3 className="font-heading font-semibold text-[15px]">{title}</h3>
    </div>
    <dl className="space-y-2">
      {items.map(([k, v]) => (
        <div key={k} className="flex justify-between text-sm border-b border-gray-100 last:border-0 pb-1.5 last:pb-0">
          <dt className="text-gray-500">{k}</dt>
          <dd className="font-semibold text-gray-900 text-right ml-3">{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const OroStat = ({ label, value, highlight, accent }) => {
  const accentColor = accent === "up" ? "text-red-700" : accent === "down" ? "text-green-700" : "text-gray-900";
  return (
    <div className={`p-3 rounded-lg ${highlight ? "bg-gray-900 text-white" : "bg-gray-50"}`}>
      <div className={`text-[10px] uppercase font-bold tracking-wider ${highlight ? "text-gray-400" : "text-gray-500"}`}>
        {label}
      </div>
      <div className={`font-heading font-bold text-lg mt-0.5 ${highlight ? "text-white" : accentColor}`}>
        {value}
      </div>
    </div>
  );
};

const ListCard = ({ title, icon: Icon, items, tone = "blue" }) => {
  const toneClass = tone === "amber" ? "bg-amber-50 text-amber-800" : "bg-blue-50 text-blue-900";
  return (
    <div className="crtm-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneClass}`}>
          <Icon size={15} strokeWidth={1.75} />
        </div>
        <h3 className="font-heading font-semibold text-[15px]">{title}</h3>
      </div>
      <ul className="space-y-2 text-sm text-gray-700 leading-relaxed">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-gray-400 mt-0.5">·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
